import { getNotificationPopup } from "@reactive-resume/dto";
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";

import { toast } from "@/client/hooks/use-toast";
import { socket } from "@/client/libs/socket";

import { markNotificationAsRead, type NotificationItem } from "./api";
import { getEventDisplay } from "./event-display";
import { updateNotificationBadges } from "./badges";
import { NOTIFICATION_KEYS } from "./queries";
import { useNotificationBody } from "./use-notification-body";

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  PONT TEMPS RÉEL — socket.io ⇄ TanStack Query + toasts
 * ─────────────────────────────────────────────────────────────────────────────
 *  À monter UNE SEULE FOIS (NotificationRealtimeBridge dans les providers).
 *  - `notification:new` : nouvelle notification (room user:{id})
 *  - `connection:established` : reconnexion → resynchronisation du cache
 *  - `admin:alert` : signal temps réel pour les dashboards admin
 * ─────────────────────────────────────────────────────────────────────────────
 */
export { updateNotificationBadges };

export const NotificationRealtimeBridge = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const notificationBody = useNotificationBody();

  useEffect(() => {
    const invalidate = () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.all });
      // Les files d'attente ops (badges sidebar + widget « À traiter ») doivent
      // se recalculer à chaque alerte : un badge périmé fait manquer une demande.
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.adminQueues });
    };

    const onNewNotification = (notification: NotificationItem) => {
      invalidate();

      // Pas de toast pour les notifications marquées `silent` (bruit de fond)
      if (notification.silent) return;

      // Affichée en fenêtre par NotificationPopupHost.
      if (getNotificationPopup(notification.data)) return;

      const display = getEventDisplay(notification.type);
      const Icon = display.icon;

      // Une alerte critique (argent sortant, demande d'aide) reste affichée
      // plus longtemps : l'admin doit avoir le temps de la lire et d'agir.
      const isCritical =
        notification.type === "ADMIN_WITHDRAWAL_REQUEST" ||
        notification.type === "ADMIN_HELP_ALERT";
      const isAdminAlert = notification.type.startsWith("ADMIN_");

      toast({
        icon: (
          <span
            className={`grid size-8 place-items-center rounded-lg ${isCritical ? "bg-amber-500/15 text-amber-600" : "bg-canvas-subtle text-text"}`}
          >
            <Icon size={16} weight={isCritical ? "fill" : "regular"} />
          </span>
        ),
        title: `${isAdminAlert ? "Action requise — " : ""}${notification.title ?? display.label}`,
        description: notificationBody(notification),
        duration: isCritical ? 20_000 : undefined,
        action: notification.link
          ? {
              label: isCritical ? "Traiter" : "Ouvrir",
              onClick: () => {
                if (!notification.isRead) {
                  void markNotificationAsRead(notification.id)
                    .then(invalidate)
                    .catch(() => null);
                }
                navigate(notification.link as string);
              },
            }
          : undefined,
      });
    };

    socket.on("notification:new", onNewNotification);
    socket.on("connection:established", invalidate);
    socket.on("admin:alert", invalidate);

    return () => {
      socket.off("notification:new", onNewNotification);
      socket.off("connection:established", invalidate);
      socket.off("admin:alert", invalidate);
    };
  }, [queryClient, navigate, notificationBody]);

  return null;
};
